import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Alert,
  Chip,
  CircularProgress
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useStripe, useElements, CardElement } from '@stripe/react-stripe-js';
import { fetcher, mutation } from '../../utils/api';
import { ApiRoutes } from '../../constants/routes';
import { useColors } from '../../constants/colors';
import { useAuthProvider } from '../../utils/auth';

const DepositDialog = ({ open, onClose }) => {
  const Colors = useColors();
  const { isActListeningMode, currentActId } = useAuthProvider();
  const [amount, setAmount] = useState('');
  const [error, setError] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [clientSecret, setClientSecret] = useState('');
  const [paymentIntentId, setPaymentIntentId] = useState('');
  const [showCardForm, setShowCardForm] = useState(false);
  const [cardComplete, setCardComplete] = useState(false);
  const [cardElementReady, setCardElementReady] = useState(false);
  const [cardElementRef, setCardElementRef] = useState(null);
  const queryClient = useQueryClient();
  // Newcomers may start small; the server enforces the same minimum
  const { data: creditStatus } = useQuery(
    ['deposit-minimum'],
    () => fetcher(ApiRoutes.UserCreditStatus),
    { enabled: open, staleTime: 0 }
  );
  const minimum = creditStatus?.minimum_deposit || 10;
  const presets = [minimum, 10, 20, 50].filter((value, i, all) => value >= minimum && all.indexOf(value) === i);
  const stripe = useStripe();
  const elements = useElements();

  // Debug logging (can be removed in production)
  // console.log('DepositDialog render - open:', open, 'stripe:', stripe, 'elements:', elements, 'showCardForm:', showCardForm);

  // Monitor showCardForm changes
  useEffect(() => {
    // console.log('showCardForm changed to:', showCardForm);
    if (showCardForm) {
      // console.log('Card form should be visible now. Stripe:', stripe, 'Elements:', elements);
    }
  }, [showCardForm, stripe, elements]);

  // Safety timeout to prevent form from getting stuck in processing state
  useEffect(() => {
    if (isProcessing) {
      const timeout = setTimeout(() => {
        console.warn('Processing timeout - resetting processing state');
        setIsProcessing(false);
        setError('Request timed out. Please try again.');
      }, 30000); // 30 second timeout

      return () => clearTimeout(timeout);
    }
  }, [isProcessing]);

  const createDepositMutation = useMutation({
    mutationFn: async (depositData) => {
      const result = await mutation(ApiRoutes.DepositTransactionCreate, depositData, 'POST');
      return result;
    },
    onSuccess: (data) => {
      console.log('Deposit created successfully, showing card form:', data);
      setClientSecret(data.client_secret);
      setPaymentIntentId(data.payment_intent_id);
      setShowCardForm(true);
      setIsProcessing(false); // Reset processing state when showing card form
    },
    onError: (error) => {
      setError(error.message || 'Failed to create deposit');
      setIsProcessing(false);
    }
  });

  const confirmPaymentMutation = useMutation({
    mutationFn: async ({ clientSecret, paymentMethod, paymentIntentId }) => {
      // Check if we're in development mode (mock payment intent)
      if (paymentIntentId.startsWith('pi_dev_')) {
        console.log('Development mode: Bypassing Stripe confirmation');
        // In development mode, skip Stripe confirmation and go directly to backend
        const confirmResult = await mutation(ApiRoutes.ConfirmPayment, {
          payment_intent_id: paymentIntentId
        }, 'POST');
        
        return { success: true, confirmResult };
      }
      
      // Production mode: Use real Stripe confirmation
      const { error } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: paymentMethod.id
      });
      if (error) throw error;
      
      // Payment is already confirmed by Stripe, just call backend to update balance
      const confirmResult = await mutation(ApiRoutes.ConfirmPayment, {
        payment_intent_id: paymentIntentId
      }, 'POST');
      
      return { success: true, confirmResult };
    },
    onSuccess: async () => {
      queryClient.invalidateQueries(['userCreditBalance']);
      // Invalidate and refetch act credit balance if in act listening mode
      if (isActListeningMode && currentActId) {
        queryClient.invalidateQueries(['actCreditBalance', currentActId]);
        await queryClient.refetchQueries(['actCreditBalance', currentActId]);
      } else {
        // Invalidate all act credit balance queries as fallback
        queryClient.invalidateQueries(['actCreditBalance']);
      }
      queryClient.invalidateQueries(['deposit-transactions']);
      queryClient.invalidateQueries(['deposit-minimum']);
      onClose();
      setAmount('');
      setError('');
      setShowCardForm(false);
      setClientSecret('');
      setPaymentIntentId('');
    },
    onError: (error) => {
      setError(error.message || 'Payment failed');
      setIsProcessing(false);
    }
  });

  const handleDeposit = async () => {
    if (!amount || amount <= 0) {
      setError('Please enter a valid amount');
      return;
    }
    if (parseFloat(amount) < minimum) {
      setError(`The minimum deposit is $${minimum}`);
      return;
    }

    setError('');
    setIsProcessing(true);

    try {
      const depositData = {
        amount: parseFloat(amount)
      };
      
      // Include act_id if in act listening mode
      if (isActListeningMode && currentActId) {
        depositData.act_id = currentActId;
      }
      
      await createDepositMutation.mutateAsync(depositData);
    } catch (error) {
      console.error('Deposit error:', error);
      setIsProcessing(false); // Ensure processing state is reset on error
      setError(error.message || 'Failed to create deposit');
    }
  };

  const handlePayment = async () => {
    if (!stripe || !elements) {
      setError('Stripe not loaded. Please refresh the page.');
      return;
    }

    setIsProcessing(true);
    setError('');

    const cardElement = elements.getElement(CardElement);

    try {
      console.log('Creating payment method...');
      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
      });

      if (error) {
        console.error('Payment method creation error:', error);
        setError(error.message);
        setIsProcessing(false);
        return;
      }

      console.log('Payment method created:', paymentMethod.id);
      console.log('Confirming payment with client secret:', clientSecret);

      await confirmPaymentMutation.mutateAsync({
        clientSecret,
        paymentMethod,
        paymentIntentId
      });
    } catch (error) {
      console.error('Payment error:', error);
      setError(error.message || 'Payment failed. Please try again.');
      setIsProcessing(false);
    }
  };

  const handleCardChange = (event) => {
    // console.log('CardElement onChange event:', event);
    setCardComplete(event.complete);
    if (event.error) {
      console.log('CardElement error:', event.error);
      setError(event.error.message);
    } else {
      setError('');
    }
  };

  const handleClose = () => {
    if (!isProcessing) {
      onClose();
      setAmount('');
      setError('');
      setShowCardForm(false);
      setClientSecret('');
      setPaymentIntentId('');
      setCardComplete(false);
      setCardElementReady(false);
      setCardElementRef(null);
    }
  };

  const cardElementOptions = {
    style: {
      base: {
        fontSize: '16px',
        color: '#424770',
        fontFamily: 'Inter, sans-serif',
        backgroundColor: '#ffffff',
        '::placeholder': {
          color: '#aab7c4',
        },
      },
      invalid: {
        color: '#9e2146',
      },
    },
    hidePostalCode: false, // Show postal code field
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ color: Colors.mainYellow, fontFamily: 'Inter', fontWeight: 'bold' }}>
        Make Deposit
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          {!showCardForm ? (
            <>
              <Typography variant="body2" sx={{ fontFamily: 'Inter', mb: 2 }}>
                Enter the amount you'd like to add to your account:
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                {presets.map((value) => (
                  <Chip
                    key={value}
                    label={`$${value}`}
                    onClick={() => setAmount(String(value))}
                    color={parseFloat(amount) === value ? 'primary' : 'default'}
                    variant={parseFloat(amount) === value ? 'filled' : 'outlined'}
                    disabled={isProcessing}
                  />
                ))}
              </Box>

              <TextField
                fullWidth
                label="Amount ($)"
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                inputProps={{ min: minimum, step: 1 }}
                disabled={isProcessing}
                sx={{ mb: 2 }}
              />

              <Typography variant="caption" sx={{ fontFamily: 'Inter', color: 'text.secondary' }}>
                Minimum deposit: ${minimum}
                {minimum < 10 && ' for your first deposit, then $10'}
              </Typography>
            </>
          ) : (
            <>
              <Typography variant="body2" sx={{ fontFamily: 'Inter', mb: 2 }}>
                Amount: ${amount}
              </Typography>
              
              <Typography variant="body2" sx={{ fontFamily: 'Inter', mb: 2 }}>
                Enter your card details:
              </Typography>
              
              {cardElementReady && (
                <Typography variant="caption" sx={{ fontFamily: 'Inter', color: 'green', mb: 1, display: 'block' }}>
                  ✓ Card form is ready for input
                </Typography>
              )}
              
              
              <Box 
                onClick={() => {
                  if (cardElementRef) {
                    cardElementRef.focus();
                  }
                }}
                sx={{ 
                  p: 2, 
                  border: '2px solid #e0e0e0', 
                  borderRadius: 2, 
                  mb: 2,
                  backgroundColor: '#ffffff',
                  minHeight: '60px',
                  display: 'flex',
                  alignItems: 'center',
                  position: 'relative',
                  cursor: 'text',
                  '&:focus-within': {
                    borderColor: Colors.mainYellow,
                    boxShadow: `0 0 0 2px ${Colors.mainYellow}20`
                  }
                }}
              >
                {/* {console.log('Rendering CardElement with stripe:', stripe, 'elements:', elements)} */}
                {!stripe || !elements ? (
                  <Typography variant="body2" sx={{ color: 'red' }}>
                    Stripe not loaded. Stripe: {stripe ? 'loaded' : 'not loaded'}, Elements: {elements ? 'loaded' : 'not loaded'}
                  </Typography>
                ) : (
                  <>
                    {!cardElementReady && (
                      <Typography variant="body2" sx={{ 
                        color: '#666', 
                        position: 'absolute',
                        left: '16px',
                        pointerEvents: 'none'
                      }}>
                        Click here to enter card details...
                      </Typography>
                    )}
                    <Box sx={{ 
                      width: '100%', 
                      height: '100%',
                      '& .StripeElement': {
                        width: '100%',
                        height: '100%'
                      }
                    }}>
                      <CardElement 
                        options={cardElementOptions} 
                        onChange={handleCardChange}
                        onReady={(element) => {
                          // console.log('CardElement is ready');
                          setCardElementReady(true);
                          setCardElementRef(element);
                        }}
                        onFocus={() => {/* console.log('CardElement focused') */}}
                        onBlur={() => {/* console.log('CardElement blurred') */}}
                      />
                    </Box>
                  </>
                )}
              </Box>
              
              <Typography variant="caption" sx={{ fontFamily: 'Inter', color: 'gray', mb: 2, display: 'block' }}>
                For test cards, use any valid postal code (e.g., 12345)
              </Typography>
              
              <Box sx={{ 
                p: 2, 
                backgroundColor: '#f8f9fa', 
                borderRadius: 1, 
                mb: 2,
                border: '1px solid #dee2e6'
              }}>
                <Typography variant="caption" sx={{ fontFamily: 'Inter', fontWeight: 'bold', display: 'block', mb: 1, color: '#212529' }}>
                  Test Card Information:
                </Typography>
                <Typography variant="caption" sx={{ fontFamily: 'Inter', display: 'block', color: '#495057', mb: 0.5 }}>
                  Card Number: 4242 4242 4242 4242
                </Typography>
                <Typography variant="caption" sx={{ fontFamily: 'Inter', display: 'block', color: '#495057', mb: 0.5 }}>
                  Expiry: Any future date (e.g., 12/25)
                </Typography>
                <Typography variant="caption" sx={{ fontFamily: 'Inter', display: 'block', color: '#495057', mb: 0.5 }}>
                  CVC: Any 3 digits (e.g., 123)
                </Typography>
                <Typography variant="caption" sx={{ fontFamily: 'Inter', display: 'block', color: '#495057' }}>
                  Postal Code: Any valid code (e.g., 12345)
                </Typography>
              </Box>
            </>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 3 }}>
        <Button 
          onClick={handleClose} 
          disabled={isProcessing}
          sx={{ color: Colors.mainYellow }}
        >
          Cancel
        </Button>
        <Button
          onClick={showCardForm ? handlePayment : handleDeposit}
          disabled={isProcessing || (!showCardForm && !amount) || (showCardForm && !cardComplete)}
          variant="contained"
          sx={{ 
            backgroundColor: Colors.mainYellow,
            color: 'black',
            '&:hover': {
              backgroundColor: Colors.mainYellow,
              opacity: 0.9
            }
          }}
        >
          {isProcessing ? (
            <>
              <CircularProgress size={20} sx={{ mr: 1 }} />
              Processing...
            </>
          ) : showCardForm ? (
            'Complete Payment'
          ) : (
            'Continue to Payment'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DepositDialog;
